import { notificationRoute } from './push-route';
import { initializeApp, getApps } from 'firebase/app';
import { deleteToken, getMessaging, getToken, isSupported, onMessage, type Messaging } from 'firebase/messaging';
import { ref, readonly } from 'vue';
import { z } from 'zod';
import { getSession, onSessionChange } from '@/core';
import { httpClient } from './httpClient';
import workerUrl from './push-worker?worker&url';

const ConfigSchema = z.object({
  VITE_FIREBASE_API_KEY: z.string().min(1),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().min(1),
  VITE_FIREBASE_APP_ID: z.string().min(1),
  VITE_FIREBASE_VAPID_KEY: z.string().min(1),
});
export type PushState = 'idle' | 'busy' | 'enabled' | 'denied' | 'unsupported' | 'unconfigured' | 'error' | 'cleaning';
const state = ref<PushState>('idle');
export const pushState = readonly(state);
export const pushLabels: Readonly<Record<PushState, string>> = {
  cleaning: 'Concluindo a desativação das notificações. Aguarde.',
  idle: 'Ainda não ativadas', busy: 'Ativando notificações…', enabled: 'Ativadas',
  denied: 'Bloqueadas pelo dispositivo', unsupported: 'Indisponíveis neste dispositivo',
  unconfigured: 'Notificações ainda indisponíveis', error: 'Falha ao ativar. Tente novamente.',
};
const message = ref<{ readonly route: string } | null>(null);
export const pushMessage = readonly(message);
let generation = 0;
function withinDeadline<T>(operation: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => { reject(new Error('Tempo de resposta das notificações excedido.')); }, 5000);
    operation.then(resolve, reject).finally(() => { window.clearTimeout(timer); }).catch(() => {});
  });
}
let messaging: Messaging | null = null;
let registration: ServiceWorkerRegistration | null = null;
let pending: Promise<void> | null = null;
let stopping = false;
function isStopping(): boolean { return stopping; }
let started = false;
let currentToken: string | null = null;
function config() {
  return ConfigSchema.safeParse({ ...import.meta.env, ...window.__ENV__ });
}
async function prepare(): Promise<boolean> {
  const parsed = config();
  if (!parsed.success) { state.value = 'unconfigured'; return false; }
  if (!('Notification' in window) || !await withinDeadline(isSupported())) { state.value = 'unsupported'; return false; }
  if (messaging !== null && registration !== null) return true;
  const value = parsed.data;
  const options = { apiKey: value.VITE_FIREBASE_API_KEY, projectId: value.VITE_FIREBASE_PROJECT_ID,
    messagingSenderId: value.VITE_FIREBASE_MESSAGING_SENDER_ID, appId: value.VITE_FIREBASE_APP_ID };
  const app = getApps().find((item) => item.name === 'dentistech-push') ?? initializeApp(options, 'dentistech-push');
  messaging = getMessaging(app);
  const url = new URL(workerUrl, window.location.origin);
  url.searchParams.set('config', JSON.stringify(options));
  registration = await withinDeadline(navigator.serviceWorker.register(url.href, { scope: '/' }));
  await withinDeadline(navigator.serviceWorker.ready);
  onMessage(messaging, (payload) => {
    if (getSession().user === null || isStopping()) return;
    message.value = { route: notificationRoute(payload.data, 'tecnicos') };
  });
  return true;
}
async function enable(requestPermission: boolean): Promise<void> {
  const user = getSession().user;
  if (user === null || stopping) return;
  const operationGeneration = generation;
  state.value = 'busy';
  try {
    // A permissão deve começar no gesto do usuário (Safari), antes de aguardar IndexedDB/SW.
    const permissionRequest = requestPermission && config().success && 'Notification' in window
      ? Notification.requestPermission() : null;
    if (!await prepare() || messaging === null || registration === null) return;
    const permission = permissionRequest === null ? Notification.permission : await permissionRequest;
    if (permission !== 'granted') { state.value = permission === 'denied' ? 'denied' : 'idle'; return; }
    const parsed = config();
    if (!parsed.success) { state.value = 'unconfigured'; return; }
    const token = await withinDeadline(getToken(messaging, { vapidKey: parsed.data.VITE_FIREBASE_VAPID_KEY, serviceWorkerRegistration: registration }));
    if (token === '' || operationGeneration !== generation || isStopping() || getSession().user?.id !== user.id) return;
    await httpClient.post<unknown>('/notifications/device-tokens', { technicianId: user.id, token, platform: 'web' });
    currentToken = token;
    if (operationGeneration !== generation || isStopping()) return;
    state.value = 'enabled';
  } catch { state.value = isStopping() ? 'cleaning' : 'error'; }
}
export function enablePushNotifications(requestPermission = true): Promise<void> {
  if (pending !== null) return pending;
  pending = enable(requestPermission).finally(() => { pending = null; });
  return pending;
}
/** Chamado ANTES de limpar CSRF/sessão; mesmo com backend indisponível invalida o token no FCM. */
export async function disablePushNotifications(): Promise<void> {
  const ownerId = getSession().user?.id;
  generation += 1;
  stopping = true;
  state.value = 'cleaning';
  message.value = null;
  const cleanup = async () => {
  try {
    await pending;
    if (!await prepare() || messaging === null || registration === null || Notification.permission !== 'granted') return;
    const parsed = config();
    if (!parsed.success) return;
    const token = currentToken ?? await getToken(messaging, { vapidKey: parsed.data.VITE_FIREBASE_VAPID_KEY, serviceWorkerRegistration: registration });
    try {
      if (token !== '' && getSession().user?.id === ownerId) await httpClient.delete<unknown>(`/notifications/device-tokens/${encodeURIComponent(token)}`);
    } finally { await deleteToken(messaging); }
  } finally { currentToken = null; state.value = 'idle'; }
  };
  // A saída do portal é limitada a 5s; uma limpeza tardia mantém novos registros suspensos
  // até terminar, evitando revogar o token de outra conta que entrou nesse intervalo.
  const cleanupOperation = cleanup().finally(() => { stopping = false; });
  try { await withinDeadline(cleanupOperation); } catch (error) { state.value = isStopping() ? 'cleaning' : 'error'; throw error; }
}
export function initializePushNotifications(): void {
  if (started) return;
  started = true;
  if (!config().success) state.value = 'unconfigured';
  else if (!('Notification' in window)) state.value = 'unsupported';
  else if (Notification.permission === 'denied') state.value = 'denied';
  const renew = () => {
    if (getSession().user !== null && 'Notification' in window && Notification.permission === 'granted') {
      void enablePushNotifications(false);
    }
  };
  onSessionChange(renew);
  window.addEventListener('focus', renew);
  renew();
}